import { useEffect, useState } from "react";
import {
	type FieldPath,
	type FieldValues,
	type UseControllerProps,
	useController,
} from "react-hook-form";
import { FormFieldError } from "../FormFieldError";
import { Input } from "../inputs/Input";

export type FormPercentProps<
	TFieldValues extends FieldValues,
	TTransformedValues = TFieldValues,
> = UseControllerProps<TFieldValues, FieldPath<TFieldValues>, TTransformedValues> & {
	compact?: boolean;
	className?: string;
	containerClassName?: string;
	placeholder?: string;
	disabled?: boolean;
	errorMessage?: string;
	hideErrorMessage?: boolean;
	min?: number;
	max?: number;
	allowDecimals?: boolean;
	decimalPlaces?: number;
	testId?: string;
	id?: string;
	/**
	 * Store `null` instead of `undefined` when the field is emptied. React Hook Form reads an
	 * `undefined` value as unset and shows the value the field mounted with, so a cleared prefill
	 * would come back.
	 */
	clearsToNull?: boolean;
};

export const FormPercent = <TFieldValues extends FieldValues, TTransformedValues = TFieldValues>({
	name,
	control,
	defaultValue,
	compact,
	className,
	containerClassName,
	placeholder = "0",
	disabled,
	errorMessage,
	hideErrorMessage = false,
	min = 0,
	max = 100,
	allowDecimals = true,
	decimalPlaces,
	testId,
	id,
	clearsToNull = false,
}: FormPercentProps<TFieldValues, TTransformedValues>) => {
	const {
		field: { onChange, onBlur, value, ...field },
		fieldState: { error },
	} = useController<TFieldValues, FieldPath<TFieldValues>, TTransformedValues>({
		name,
		control,
		defaultValue,
	});

	const hasError = !!error;
	const emptyValue = clearsToNull ? null : undefined;

	const [displayValue, setDisplayValue] = useState<string>("");
	const [isFocused, setIsFocused] = useState(false);

	useEffect(() => {
		if (!isFocused) {
			if (value != null && typeof value === "number") {
				const displayNum = Number.parseFloat((value * 100).toFixed((decimalPlaces || 1) + 2));
				setDisplayValue(displayNum.toString());
			} else {
				setDisplayValue("");
			}
		}
	}, [value, isFocused]);

	const handleFocus = () => {
		setIsFocused(true);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const inputValue = e.target.value;
		const decimalLimit = decimalPlaces || 1;
		const pattern = allowDecimals ? new RegExp(`^\\d*\\.?\\d{0,${decimalLimit}}$`) : /^\d*$/;

		if (!pattern.test(inputValue)) return;

		setDisplayValue(inputValue);

		if (inputValue === "") {
			onChange(emptyValue);
			return;
		}

		const numValue = parseFloat(inputValue);
		const isIntermediateState =
			inputValue.endsWith(".") ||
			inputValue.endsWith(".0") ||
			(inputValue.includes(".") && inputValue !== numValue.toString());

		if (!isNaN(numValue) && !isIntermediateState) {
			let constrainedValue = numValue;
			if (min !== undefined && numValue < min) constrainedValue = min;
			if (max !== undefined && numValue > max) constrainedValue = max;
			onChange(Number.parseFloat((constrainedValue / 100).toFixed((decimalPlaces || 1) + 2)));
		}
	};

	const handleBlur = () => {
		setIsFocused(false);
		const numValue = parseFloat(displayValue);
		if (!isNaN(numValue)) {
			let constrainedValue = numValue;
			if (min !== undefined && numValue < min) constrainedValue = min;
			if (max !== undefined && numValue > max) constrainedValue = max;
			const decimalLimit = decimalPlaces || 1;
			if (allowDecimals) {
				const multiplier = Math.pow(10, decimalLimit);
				constrainedValue = Math.round(constrainedValue * multiplier) / multiplier;
			} else {
				constrainedValue = Math.round(constrainedValue);
			}
			onChange(Number.parseFloat((constrainedValue / 100).toFixed((decimalPlaces || 1) + 2)));
		} else if (displayValue === "") {
			onChange(emptyValue);
		}
		onBlur();
	};

	return (
		<div className="relative">
			<Input
				{...field}
				id={id}
				value={displayValue}
				onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				compact={compact}
				className={className}
				containerClassName={containerClassName}
				placeholder={placeholder}
				disabled={disabled}
				type="text"
				inputMode="decimal"
				error={hasError}
				rightAlign
				suffix="%"
				testId={testId}
			/>
			{hasError && !hideErrorMessage && (
				<FormFieldError error={error} errorMessage={errorMessage} />
			)}
		</div>
	);
};
